import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useLanguage } from '../contexts/LanguageContext';

const characterLimit = 500; // Adjust the limit as needed

export default function ContactUs() {
  const { translations } = useLanguage();

  const [formValues, setFormValues] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [characterCount, setCharacterCount] = React.useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    if (newValue.length <= characterLimit) {
      setFormValues({
        ...formValues,
        [e.target.name]: newValue,
      });
      setCharacterCount(newValue.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      await response.json();
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error:', error);
      setSubmitSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      id="contact"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Typography
        component="h2"
        variant="h4"
        sx={{
          color: 'text.primary',
          textAlign: 'center',
        }}
      >
        {translations.contact_section.title}
      </Typography>
      {submitSuccess && (
        <Typography variant="subtitle1" sx={{ color: 'green' }}>
          {translations.contact_section.success_message}
        </Typography>
      )}
      {isLoading && (
        <CircularProgress sx={{ mx: 'auto', mt: 2 }} />
      )}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: { xs: '100%', sm: '70%' },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <TextField
          name="name"
          label={translations.contact_section.name_placeholder}
          variant="outlined"
          fullWidth
          value={formValues.name}
          onChange={handleChange}
          required
        />
        <TextField
          name="email"
          label={translations.contact_section.email_placeholder}
          type="email"
          variant="outlined"
          fullWidth
          value={formValues.email}
          onChange={handleChange}
          required
        />
        <TextField
          name="subject"
          label={translations.contact_section.subject_placeholder}
          variant="outlined"
          fullWidth
          value={formValues.subject}
          onChange={handleChange}
          required
        />
        <TextField
          name="message"
          label={translations.contact_section.message_placeholder}
          multiline
          rows={8}
          variant="outlined"
          fullWidth
          value={formValues.message}
          onChange={handleChange}
          required
          sx={{
            '& .MuiInputBase-root': {
              minHeight: '150px',
              paddingTop: '45px',
            },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {characterCount}/{characterLimit} {translations.contact_section.characters}
        </Typography>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{
            fontSize: '1rem',
            padding: '12px',
            mt: 2,
          }}
        >
          {translations.contact_section.send_button}
        </Button>
      </Box>
    </Container>
  );
}
